import React from 'react';
import { useNavigate } from 'react-router-dom';
import './styles/landing.css';

/**
 * Landing page: public marketing/welcome page
 * 
 * Architected to be easily separable into a standalone site or regional variant.
 * This component is stateless and contains only presentation logic.
 * 
 * Future: Can be extracted to separate Next.js app or region-specific content
 * with minimal refactoring.
 */
export const Landing: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-logo">Zurvit</div>
          <nav className="landing-nav">
            <button
              type="button"
              className="landing-nav-link"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
          </nav>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-container">
          <div className="landing-hero-content">
            <h1>Incident management for modern teams</h1>
            <p>
              Streamline your on-call workflows, escalate with confidence, and resolve
              incidents faster with Zurvit's intelligent scheduling and alert orchestration.
            </p>
            <button
              type="button"
              className="landing-cta"
              onClick={() => navigate('/onboarding')}
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      <section className="landing-features">
        <div className="landing-container">
          <h2>Why Zurvit</h2>
          <div className="landing-feature-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📋</div>
              <h3>Unified Schedules</h3>
              <p>
                Create primary and escalation schedules with timezone support,
                automatic rotations, and real-time availability tracking.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🚨</div>
              <h3>Smart Escalation</h3>
              <p>
                Route alerts to the right person, right now. Smart escalation rules
                ensure critical incidents reach on-call engineers instantly.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">⚙️</div>
              <h3>Workflow Automation</h3>
              <p>
                Automate alert enrichment, routing, and remediation. Reduce noise,
                cut MTTR, and improve team efficiency.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔗</div>
              <h3>Multi-Source Integration</h3>
              <p>
                Connect your existing monitoring tools: Datadog, PagerDuty, Grafana,
                CloudWatch, and more.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-cta-section">
        <div className="landing-container">
          <h2>Ready to streamline your on-call?</h2>
          <p>Join teams using Zurvit to manage incidents faster.</p>
          <button
            type="button"
            className="landing-cta landing-cta--large"
            onClick={() => navigate('/login')}
          >
            Sign In to Your Account
          </button>
          <p className="landing-signup-link">
            New to Zurvit?{' '}
            <button
              type="button"
              className="landing-link-button"
              onClick={() => navigate('/onboarding')}
            >
              Create your workspace
            </button>
          </p>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container">
          <p>&copy; 2026 Zurvit. Incident management for modern teams.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
